import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { VendedorHierarquiaStruct } from '../../../../../shared/services/structs/pessoa/vendedor-hierarquia.struct';
import { VendedorHierarquiaHelper } from '../../../../../shared/helpers/vendedor-hierarquia.helper';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { VendedorHistoricoTipoEnum } from '../../../../../shared/enums/pessoa/vendedor-historico-tipo.enum';
import { SituacaoVendedorEnum } from '../../../../../shared/enums/pessoa/situacao-vendedor.enum';

interface OpcaoEquipe {
  idVendedor: number;
  nome: string;
  detalhe: string;
  indisponivel: boolean;
}

interface LinhaHistoricoPendente {
  tipo: VendedorHistoricoTipoEnum;
  icone: string;
  texto: string;
}

// Story, seção 3: bloco "Equipe (subordinados diretos)" — componente dedicado (não é o
// app-select-pesquisavel genérico: precisa de detalhe de impacto por linha, motivo de
// indisponibilidade e painel de alterações pendentes, nada disso o componente genérico suporta).
@Component({
  selector: 'app-vendedor-equipe-seletor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, MatFormFieldModule, MatInputModule],
  templateUrl: './vendedor-equipe-seletor.component.html',
  styleUrl: './vendedor-equipe-seletor.component.css',
})
export class VendedorEquipeSeletorComponent implements OnChanges {
  // Vendedor sendo editado (null enquanto o cadastro ainda não foi salvo — Story, seção 3.1).
  @Input() idVendedor: number | null = null;
  // Valor atual (ainda não salvo) do campo "Vendedor Responsável" no formulário.
  @Input() idVendedorResponsavelAtual: number | null = null;
  @Input() flatHierarquia: VendedorHierarquiaStruct[] = [];
  // Ids que hoje (no banco) respondem a idVendedor — ponto de partida do diff.
  @Input() membrosIniciais: number[] = [];

  @Output() equipeChange = new EventEmitter<number[]>();

  public buscaControl = new FormControl('');
  public dropdownAberto = false;
  public membros: number[] = [];
  public historicoPendente: LinhaHistoricoPendente[] = [];

  constructor(private alertService: AlertService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['membrosIniciais']) {
      this.membros = [...this.membrosIniciais];
      this.recalcularHistorico();
    }
  }

  private get porId(): Map<number, VendedorHierarquiaStruct> {
    return new Map(this.flatHierarquia.map(v => [v.idVendedor, v]));
  }

  public get opcoesFiltradas(): OpcaoEquipe[] {
    const termo = (this.buscaControl.value || '').trim().toLowerCase();
    const ancestraisDeMim = this.idVendedor != null
      ? new Set(VendedorHierarquiaHelper.getAncestrais(this.idVendedor, this.flatHierarquia).map(a => a.idVendedor))
      : new Set<number>();

    // Inativos não entram como opção nova — exceto quem já está na equipe (membrosIniciais),
    // pra não sumir do bloco se foi inativado depois de vinculado (Story: BUG 8).
    return this.flatHierarquia
      .filter(v => v.idSituacaoVendedor !== SituacaoVendedorEnum.Inativo || this.membrosIniciais.includes(v.idVendedor))
      .filter(v => !this.membros.includes(v.idVendedor))
      .filter(v => !termo || v.nome.toLowerCase().includes(termo))
      .map(v => this.montarOpcao(v, ancestraisDeMim))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }

  private montarOpcao(v: VendedorHierarquiaStruct, ancestraisDeMim: Set<number>): OpcaoEquipe {
    if (this.idVendedor != null && v.idVendedor === this.idVendedor) {
      return { idVendedor: v.idVendedor, nome: v.nome, indisponivel: true, detalhe: 'Indisponível — próprio cadastro' };
    }
    if (this.idVendedorResponsavelAtual != null && v.idVendedor === this.idVendedorResponsavelAtual) {
      return { idVendedor: v.idVendedor, nome: v.nome, indisponivel: true, detalhe: 'Indisponível — já é o Vendedor Responsável deste cadastro' };
    }
    if (ancestraisDeMim.has(v.idVendedor)) {
      return { idVendedor: v.idVendedor, nome: v.nome, indisponivel: true, detalhe: 'Indisponível — é superior deste vendedor' };
    }

    const responsavelAtual = v.idVendedorResponsavel != null ? this.porId.get(v.idVendedorResponsavel) : null;
    let detalhe = responsavelAtual ? `hoje sob ${responsavelAtual.nome}` : 'sem superior';
    const naCadeia = VendedorHierarquiaHelper.getDescendentes(v.idVendedor, this.flatHierarquia).size;
    if (naCadeia > 0) detalhe += ` · traz ${naCadeia} na cadeia`;

    return { idVendedor: v.idVendedor, nome: v.nome, indisponivel: false, detalhe };
  }

  public selecionar(opcao: OpcaoEquipe): void {
    if (opcao.indisponivel) {
      this.alertService.show('Não é possível incluir',
        `${opcao.nome} não pode entrar na equipe: ${opcao.detalhe.replace('Indisponível — ', '')}. Isso criaria um ciclo na hierarquia.`,
        AlertType.warning);
      return;
    }

    const vendedor = this.porId.get(opcao.idVendedor);
    if (vendedor?.idVendedorResponsavel != null) {
      const responsavelAtual = this.porId.get(vendedor.idVendedorResponsavel);
      this.alertService.show('Vendedor já tinha superior',
        `${opcao.nome} respondia a ${responsavelAtual?.nome ?? '—'}. Ao salvar, o Vendedor Responsável dele passa a ser este cadastro.`,
        AlertType.warning);
    }

    this.membros = [...this.membros, opcao.idVendedor];
    this.buscaControl.setValue('');
    this.dropdownAberto = false;
    this.recalcularHistorico();
    this.equipeChange.emit(this.membros);
  }

  public remover(idVendedor: number): void {
    this.membros = this.membros.filter(id => id !== idVendedor);
    this.recalcularHistorico();
    this.equipeChange.emit(this.membros);
  }

  public getQtdSubordinados(idVendedor: number): number {
    return VendedorHierarquiaHelper.getQtdSubordinadosDiretos(idVendedor, this.flatHierarquia);
  }

  public getNome(idVendedor: number): string {
    return this.porId.get(idVendedor)?.nome ?? '';
  }

  private recalcularHistorico(): void {
    const porId = this.porId;
    const linhas: LinhaHistoricoPendente[] = [];

    for (const id of this.membros) {
      if (this.membrosIniciais.includes(id)) continue; // já era membro, não mudou

      const vendedor = porId.get(id);
      if (!vendedor) continue;

      const responsavelAnteriorId = vendedor.idVendedorResponsavel;
      const responsavelAnterior = responsavelAnteriorId != null ? porId.get(responsavelAnteriorId) : null;

      if (responsavelAnterior) {
        linhas.push({
          tipo: VendedorHistoricoTipoEnum.TrocaEquipe, icone: 'swap_horiz',
          texto: `${vendedor.nome} deixou a equipe de ${responsavelAnterior.nome} e passou a responder por este vendedor.`
        });
      } else {
        linhas.push({
          tipo: VendedorHistoricoTipoEnum.InclusaoSimples, icone: 'person_add',
          texto: `${vendedor.nome} foi incluído na equipe.`
        });
      }

      const subordinados = this.flatHierarquia.filter(v => v.idVendedorResponsavel === id);
      if (subordinados.length > 0) {
        linhas.push({
          tipo: VendedorHistoricoTipoEnum.CadeiaArrastada, icone: 'account_tree',
          texto: `${subordinados.length} subordinado(s) de ${vendedor.nome} (${subordinados.map(s => s.nome).join(', ')}) acompanham a mudança.`
        });
      }
    }

    for (const id of this.membrosIniciais) {
      if (this.membros.includes(id)) continue; // continua membro, não mudou

      const vendedor = porId.get(id);
      if (!vendedor) continue;

      linhas.push({
        tipo: VendedorHistoricoTipoEnum.Remocao, icone: 'person_remove',
        texto: `${vendedor.nome} saiu da equipe. Ao salvar, o Vendedor Responsável dele fica em branco.`
      });
    }

    this.historicoPendente = linhas;
  }
}
